import React from 'react'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { MovieListItem } from '@scentregroup/shared/components/now-showing'
import Layout from '@scentregroup/shared/hub-components/layout'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import { DateTime } from '@scentregroup/shared/helpers/date-helpers'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import {
  GetServerSidePropsResult,
  GetServerSidePropsContext,
} from '../../../../types/next'
import { SGPageTemplateWithAdZones } from '@scentregroup/shared/types/page-templates'
import { makeApolloClient } from '../../../../lib/apolloClient'
import { getCentrePageTemplate } from '../../../../lib/page-templates/fetching'
import {
  SimpleStore,
  getCentreCinema,
} from '../../../../lib/get-content-detail/cinema'
import { MovieContentDetail } from '../../../../components/page-templates/content-detail/movie'
import {
  CentreSlug,
  StoreId,
  DateString,
  getTradingHours,
} from '../../../../lib/get-trading-hours'
import {
  getMovieDetails,
  MovieDetails,
} from '../../../../lib/get-content-detail/movie'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { shouldRedirectForMismatchedRoute } from '@scentregroup/shared/lib/redirects'

interface ICentreCinemaPageProps {
  headerStaticData: CentreStaticData
  topStripProps: ITopStripProps | null
  centre: CentreDetails
  template: SGPageTemplateWithAdZones | null
  tradingHours: TradingHourDay[]
  country: 'Australia' | 'New Zealand'
  cinema: SimpleStore
  movies: MovieListItem[]
  movie: MovieDetails
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
    id: string
    slug: string
  }>
): Promise<GetServerSidePropsResult<ICentreCinemaPageProps>> {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)
  const centreSlug = ctx.params?.centre
  const movieId = ctx.params?.id
  const slug = ctx.params?.slug[0]
  if (!centreSlug || !slug || !movieId) {
    return {
      notFound: true,
    }
  }
  const client = makeApolloClient()
  const centre = await getCentreDetails(client, { slug: centreSlug, country })
  if (!centre) {
    return {
      notFound: true,
    }
  }
  const redirectForCentreSlug = shouldRedirectForMismatchedRoute(
    '/[centre]/movie/[id]/[...slug]',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (redirectForCentreSlug) {
    return redirectForCentreSlug
  }
  const [template, headerStaticData, topStripProps] = await Promise.all([
    getCentrePageTemplate(client, centreSlug, 'movie'),
    fetchCentreStaticData(client, {
      country,
      centre,
    }),
    getTopStripProps(client, country, centreSlug),
  ])
  const contactLinks = buildContactLinks(centre)

  const [cinema, movieDetails] = await Promise.all([
    getCentreCinema(client, centre?.sys.id),
    getMovieDetails(client, centreSlug, movieId, { ...centre, contactLinks }),
  ])

  if (!cinema) {
    return {
      notFound: true,
    }
  }

  if (!movieDetails || !movieDetails.movie || !movieDetails.movies) {
    return {
      redirect: {
        destination: `/${centreSlug}/movies`,
        permanent: true,
      },
    }
  }

  const startDate = DateTime.moment()
    .tz(centre.timeZone)
    .startOf('day')
    .format('YYYY-MM-DD')
    .toString() as DateString
  const tradingHours = await getTradingHours(
    client,
    centreSlug as CentreSlug,
    cinema?.sys?.id as StoreId,
    startDate
  )

  return {
    props: {
      country,
      topStripProps,
      centre,
      cinema,
      template,
      headerStaticData,
      tradingHours,
      movie: movieDetails.movie,
      movies: movieDetails.movies,
    },
  }
}

const MoviesPage: React.FC<
  React.PropsWithChildren<ICentreCinemaPageProps>
> = props => {
  const {
    country,
    topStripProps,
    centre,
    cinema,
    template,
    headerStaticData,
    tradingHours,
    movie,
    movies,
  } = props
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const pageTitle = `${movie.title} showtimes at Westfield ${centre.title}`
  const metaData = {
    title: pageTitle,
    description: `${pageTitle}. Explore our range of movies, events & offers at Westfield ${centre.title}.`,
    canonical,
  }
  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  return (
    <Layout
      country={country}
      topStrip={topStripProps}
      centre={{
        ...centre,
        contactLinks,
      }}
      header={{
        variant: 'centre',
        data: headerStaticData,
        logoText: centre.title,
      }}
      metaData={metaData}
    >
      <MovieContentDetail
        centre={centre}
        content={movie}
        cinema={cinema}
        movies={movies}
        template={template}
        storeTradingHours={tradingHours}
        centreTradingHours={tradingHours}
      />
    </Layout>
  )
}
export default MoviesPage
